<script setup lang="ts" generic="T">
import type { Column } from '@tanstack/vue-table';
import type { Component } from 'vue';

import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from 'lucide-vue-next';

import { cn } from '@/lib/utils';

interface FilterOption {
  label: string;
  value: string;
  icon?: Component;
}

interface DataTableColumnHeaderProps {
  column: Column<T, any>;
  title: string;
  filterOptions?: FilterOption[];
}

const props = defineProps<DataTableColumnHeaderProps>();

const selectedFilters = computed(() => {
  const filterValue = props.column.getFilterValue();
  return Array.isArray(filterValue) ? filterValue : [];
});

// Auto-generate options from faceted values if no filterOptions provided
const effectiveFilterOptions = computed((): FilterOption[] => {
  // If filterOptions is explicitly set to empty array, don't show filter
  if (props.filterOptions !== undefined) {
    return props.filterOptions;
  }

  // Use faceted unique values if available (for dynamic filters like Staff)
  const facetedValues = props.column.getFacetedUniqueValues();
  if (facetedValues && facetedValues.size > 0) {
    return Array.from(facetedValues.keys())
      .filter(Boolean)
      .map(value => ({
        label: String(value),
        value: String(value),
        icon: undefined,
      }));
  }

  return [];
});

function toggleFilter(value: string) {
  const current = selectedFilters.value;
  const newValue = current.includes(value) ? current.filter(v => v !== value) : [...current, value];

  props.column.setFilterValue(newValue.length > 0 ? newValue : undefined);
}

function clearFilters() {
  props.column.setFilterValue(undefined);
}

const hasActiveFilters = computed(() => selectedFilters.value.length > 0);
const showFilter = computed(() => effectiveFilterOptions.value.length > 0);
const canSort = computed(() => props.column.getCanSort());
const hasDropdown = computed(() => canSort.value || showFilter.value);
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<template>
  <div v-if="hasDropdown" :class="cn('flex items-center space-x-2', $attrs.class ?? '')">
    <UiDropdownMenu>
      <UiDropdownMenuTrigger as-child>
        <UiButton
          variant="ghost"
          size="sm"
          :class="
            cn(
              '-ml-3 h-8 data-[state=open]:bg-accent',
              hasActiveFilters && 'text-primary font-medium',
            )
          "
        >
          <span>{{ title }}</span>
          <div class="relative">
            <ArrowDown v-if="canSort && column.getIsSorted() === 'desc'" class="size-4" />
            <ArrowUp v-else-if="canSort && column.getIsSorted() === 'asc'" class="size-4" />
            <ChevronsUpDown
              v-else-if="canSort"
              :class="cn('size-4', hasActiveFilters && 'text-primary')"
            />
            <Filter
              v-else-if="showFilter"
              :class="cn('size-4', hasActiveFilters && 'text-primary')"
            />
            <UiBadge
              v-if="hasActiveFilters"
              variant="default"
              class="absolute -top-2 -right-2 h-4 w-4 rounded-full p-0 text-[10px] leading-none flex items-center justify-center"
            >
              {{ selectedFilters.length }}
            </UiBadge>
          </div>
        </UiButton>
      </UiDropdownMenuTrigger>
      <UiDropdownMenuContent align="start" class="w-48">
        <template v-if="canSort">
          <UiDropdownMenuItem @click="column.toggleSorting(false)">
            <ArrowUp class="mr-2 size-4 text-muted-foreground/70" />
            Asc
          </UiDropdownMenuItem>
          <UiDropdownMenuItem @click="column.toggleSorting(true)">
            <ArrowDown class="mr-2 size-4 text-muted-foreground/70" />
            Desc
          </UiDropdownMenuItem>
        </template>

        <template v-if="showFilter">
          <UiDropdownMenuSeparator v-if="canSort" />
          <UiDropdownMenuLabel class="flex items-center text-xs">
            <Filter class="mr-2 size-3" />
            Filter
          </UiDropdownMenuLabel>
          <UiDropdownMenuCheckboxItem
            v-for="option in effectiveFilterOptions"
            :key="option.value"
            :model-value="selectedFilters.includes(option.value)"
            @update:model-value="() => toggleFilter(option.value)"
            @select.prevent
          >
            <component
              :is="option.icon"
              v-if="option.icon"
              class="mr-2 size-4 text-muted-foreground/70"
            />
            {{ option.label }}
          </UiDropdownMenuCheckboxItem>
          <UiDropdownMenuItem v-if="hasActiveFilters" @click="clearFilters">
            <span class="text-xs text-muted-foreground">Clear filters</span>
          </UiDropdownMenuItem>
        </template>
      </UiDropdownMenuContent>
    </UiDropdownMenu>
  </div>

  <div v-else :class="$attrs.class">
    {{ title }}
  </div>
</template>
